import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, Plus, Trash2, Search, Settings, Printer, X,
  Building2, Mail, Phone, Globe, MapPin, FileSignature, Edit, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => `${(Number(n) || 0).toFixed(2).replace('.', ',')} €`;
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const DEFAULT_SETTINGS = { legal_name: '', cif: '', address: '', phone: '', email: '', web: '', logo_url: '', bank_name: '', iban: '', swift_bic: '', shop_notification_email: '' };
const DEFAULT_CLIENT = { name: '', cif: '', address: '', email: '', phone: '' };

export default function Proforma() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('list'); // 'list' | 'settings'
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // proforma being edited
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProformas();
    fetchSettings();
  }, []);

  const fetchProformas = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/proformas`);
      setProformas(res.data);
    } catch {
      toast.error('Error cargando proformas');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/proformas/company-settings`);
      setSettings({ ...DEFAULT_SETTINGS, ...(res.data || {}) });
    } catch {}
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/proformas/company-settings`, settings);
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const MAX_LOGO_BYTES = 2 * 1024 * 1024; // 2 MB
  const ACCEPT_LOGO = '.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml';
  const handleLogoFile = (file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const okType = allowed.includes(file.type) || /\.(png|jpe?g|svg)$/i.test(file.name);
    if (!okType) {
      toast.error('Formato no permitido. Usa PNG, JPG, JPEG o SVG.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error('El logo no puede superar 2 MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSettings(prev => ({ ...prev, logo_url: String(reader.result) }));
      toast.success('Logo cargado — recuerda pulsar Guardar configuración');
    };
    reader.onerror = () => toast.error('Error leyendo el archivo');
    reader.readAsDataURL(file);
  };
  const removeLogo = () => {
    setSettings(prev => ({ ...prev, logo_url: '' }));
    toast.info('Logo eliminado — pulsa Guardar para confirmar');
  };

  const newProforma = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditorOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/proformas/${deleteId}`);
      toast.success('Proforma eliminada');
      setDeleteId(null);
      fetchProformas();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-['Barlow_Condensed'] text-3xl font-semibold text-slate-800 flex items-center gap-2">
            <FileSignature size={28} className="text-orange-500" />
            Proformas
          </h1>
          <p className="text-sm text-slate-500">Propuestas de venta de flota de bicicletas — Solo exportación PDF, sin facturación.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={tab === 'list' ? 'default' : 'outline'}
            onClick={() => setTab('list')}
            className={tab === 'list' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
            data-testid="tab-list"
          >
            <FileText size={16} className="mr-1" /> Proformas
          </Button>
          <Button
            variant={tab === 'settings' ? 'default' : 'outline'}
            onClick={() => setTab('settings')}
            className={tab === 'settings' ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
            data-testid="tab-settings"
          >
            <Settings size={16} className="mr-1" /> Configuración
          </Button>
        </div>
      </div>

      {tab === 'list' && (
        <>
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">{proformas.length} proforma{proformas.length !== 1 ? 's' : ''}</p>
            {isAdmin && (
              <Button onClick={newProforma} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="new-proforma-btn">
                <Plus size={16} /> Nueva proforma
              </Button>
            )}
          </div>
          {loading ? (
            <div className="text-center py-10 text-slate-400">Cargando...</div>
          ) : proformas.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center">
                <FileSignature size={48} className="mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 mb-3">Aún no hay proformas creadas.</p>
                {isAdmin && (
                  <Button onClick={newProforma} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="new-proforma-empty-btn">
                    <Plus size={16} /> Crear primera proforma
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead className="text-center">Líneas</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-center">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proformas.map(p => (
                      <TableRow key={p.id} data-testid={`proforma-row-${p.id}`}>
                        <TableCell className="font-mono font-semibold text-slate-700">{p.number}</TableCell>
                        <TableCell className="text-sm text-slate-500">{fmtDate(p.date)}</TableCell>
                        <TableCell>{p.client?.name || '-'}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{(p.lines || []).length}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{fmtMoney(p.total)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex justify-center gap-1">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(p)} data-testid={`edit-proforma-${p.id}`}>
                              <Edit size={14} />
                            </Button>
                            {isAdmin && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteId(p.id)} data-testid={`delete-proforma-${p.id}`}>
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === 'settings' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 size={20} className="text-orange-500" />
              Datos fiscales de la empresa
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">Se guardan una vez y aparecen automáticamente en todas las proformas.</p>
          </CardHeader>
          <CardContent className="space-y-4 max-w-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nombre fiscal *</Label>
                <Input value={settings.legal_name} onChange={(e) => setSettings({ ...settings, legal_name: e.target.value })} data-testid="settings-legal_name" />
              </div>
              <div>
                <Label>CIF / NIF</Label>
                <Input value={settings.cif} onChange={(e) => setSettings({ ...settings, cif: e.target.value })} data-testid="settings-cif" />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input value={settings.address} onChange={(e) => setSettings({ ...settings, address: e.target.value })} data-testid="settings-address" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label>Teléfono</Label>
                <Input value={settings.phone} onChange={(e) => setSettings({ ...settings, phone: e.target.value })} data-testid="settings-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={settings.email} onChange={(e) => setSettings({ ...settings, email: e.target.value })} data-testid="settings-email" />
              </div>
              <div>
                <Label>Web</Label>
                <Input value={settings.web} onChange={(e) => setSettings({ ...settings, web: e.target.value })} data-testid="settings-web" />
              </div>
            </div>
            <div className="pt-4 border-t border-slate-200">
              <h3 className="font-semibold text-sm text-slate-700 mb-3 flex items-center gap-2">
                <Building2 size={14} className="text-orange-500" />
                Datos bancarios
                <span className="text-xs font-normal text-slate-400">(aparecerán en el PDF de cada proforma)</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label>Banco</Label>
                  <Input
                    value={settings.bank_name || ''}
                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                    placeholder="BBVA, Santander..."
                    data-testid="settings-bank_name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>IBAN</Label>
                  <Input
                    value={settings.iban || ''}
                    onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                    placeholder="ES00 0000 0000 0000 0000 0000"
                    className="font-mono"
                    data-testid="settings-iban"
                  />
                </div>
                <div>
                  <Label>SWIFT / BIC</Label>
                  <Input
                    value={settings.swift_bic || ''}
                    onChange={(e) => setSettings({ ...settings, swift_bic: e.target.value })}
                    placeholder="BBVAESMM"
                    className="font-mono"
                    data-testid="settings-swift_bic"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Email para copia de reservas de la Tienda Online</Label>
                <Input
                  type="email"
                  value={settings.shop_notification_email || ''}
                  onChange={(e) => setSettings({ ...settings, shop_notification_email: e.target.value })}
                  placeholder="Si se deja vacío se usa el email de la empresa"
                  data-testid="settings-shop_notification_email"
                />
              </div>
            </div>

            <div>
              <Label className="flex items-center justify-between">
                <span>Logo de la empresa</span>
                <span className="text-xs font-normal text-slate-400">PNG · JPG · JPEG · SVG (máx. 2 MB)</span>
              </Label>
              <div className="mt-2 border border-dashed border-slate-300 rounded-lg p-4 bg-slate-50/50">
                {settings.logo_url ? (
                  <div className="flex items-start gap-4">
                    <div className="border border-slate-200 rounded-md bg-white p-2 flex items-center justify-center" style={{ width: 120, height: 80 }}>
                      <img
                        src={settings.logo_url}
                        alt="Logo"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                        data-testid="logo-preview"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm text-slate-600">
                        {settings.logo_url.startsWith('data:')
                          ? <>Logo subido desde el ordenador {settings.logo_url.includes('svg') ? '(SVG)' : ''}</>
                          : <>Logo desde URL: <span className="text-slate-400 truncate inline-block max-w-md align-bottom">{settings.logo_url}</span></>}
                      </p>
                      <div className="flex gap-2">
                        <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-100 cursor-pointer" data-testid="logo-replace-btn">
                          <Edit size={12} /> Reemplazar
                          <input
                            type="file"
                            accept={ACCEPT_LOGO}
                            className="hidden"
                            onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ''; }}
                            data-testid="logo-file-input"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-xs font-medium text-red-600 hover:bg-red-50 hover:border-red-300"
                          data-testid="logo-remove-btn"
                        >
                          <Trash2 size={12} /> Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <label className="block text-center py-6 cursor-pointer hover:bg-slate-100/70 rounded-md transition-colors" data-testid="logo-upload-area">
                    <FileText size={28} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-slate-600">Haz click para <span className="text-orange-600 font-medium">subir un logo</span></p>
                    <p className="text-xs text-slate-400 mt-1">o arrastra un archivo aquí · PNG, JPG, JPEG, SVG · máx 2 MB</p>
                    <input
                      type="file"
                      accept={ACCEPT_LOGO}
                      className="hidden"
                      onChange={(e) => { handleLogoFile(e.target.files?.[0]); e.target.value = ''; }}
                      data-testid="logo-file-input"
                    />
                  </label>
                )}
              </div>
              <details className="mt-2 text-xs">
                <summary className="text-slate-500 cursor-pointer hover:text-slate-700">o usar URL externa (opcional)</summary>
                <Input
                  value={settings.logo_url && !settings.logo_url.startsWith('data:') ? settings.logo_url : ''}
                  onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
                  placeholder="https://midominio.com/logo.png"
                  className="mt-2"
                  data-testid="settings-logo-url"
                />
              </details>
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={saveSettings} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="save-settings-btn">
                Guardar configuración
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ProformaEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        proforma={editing}
        settings={settings}
        onSaved={(saved) => { setEditing(saved); fetchProformas(); }}
      />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar proforma?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-proforma-confirm">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ===================== Proforma Editor =====================

function ProformaEditor({ open, onClose, proforma, settings, onSaved }) {
  const [client, setClient] = useState(DEFAULT_CLIENT);
  const [lines, setLines] = useState([]);
  const [ivaRate, setIvaRate] = useState(21);
  const [notes, setNotes] = useState('');
  const [bikes, setBikes] = useState([]);
  const [modelFilter, setModelFilter] = useState('all');
  const [bikeSearch, setBikeSearch] = useState('');
  const [savedProforma, setSavedProforma] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (proforma) {
      setClient({ ...DEFAULT_CLIENT, ...(proforma.client || {}) });
      setLines((proforma.lines || []).map(l => ({ ...l })));
      setIvaRate(proforma.iva_rate ?? 21);
      setNotes(proforma.notes || '');
      setSavedProforma(proforma);
    } else {
      setClient(DEFAULT_CLIENT);
      setLines([]);
      setIvaRate(21);
      setNotes('');
      setSavedProforma(null);
    }
    fetchBikes();
  }, [open, proforma]);

  const fetchBikes = async () => {
    try {
      const res = await axios.get(`${API}/proformas/_/available-bicycles`);
      setBikes(res.data);
    } catch {}
  };

  // Group bikes by model for the selector
  const models = useMemo(() => {
    const set = new Set();
    bikes.forEach(b => b.model && set.add(b.model));
    return ['all', ...[...set].sort()];
  }, [bikes]);

  const filteredBikes = useMemo(() => {
    const q = bikeSearch.trim().toLowerCase();
    return bikes.filter(b => {
      if (modelFilter !== 'all' && b.model !== modelFilter) return false;
      if (!q) return true;
      return `${b.number} ${b.model} ${b.frame_number || ''} ${b.size || ''}`.toLowerCase().includes(q);
    });
  }, [bikes, modelFilter, bikeSearch]);

  const usedBikeIds = useMemo(() => new Set(lines.map(l => l.bicycle_id).filter(Boolean)), [lines]);

  const addLine = (b) => {
    if (usedBikeIds.has(b.id)) {
      toast.info('Esa bicicleta ya está en la proforma');
      return;
    }
    setLines(prev => [...prev, {
      bicycle_id: b.id,
      bicycle_number: b.number,
      model: b.model,
      size: b.size || '',
      frame_number: b.frame_number || '',
      price_net: Number(b.manual_sale_price) || 0,
    }]);
  };

  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));
  const updateLinePrice = (idx, value) => {
    const v = parseFloat(String(value).replace(',', '.'));
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, price_net: Number.isNaN(v) ? 0 : v } : l));
  };

  const subtotal = useMemo(() => lines.reduce((s, l) => s + (Number(l.price_net) || 0), 0), [lines]);
  const ivaAmount = useMemo(() => +(subtotal * (Number(ivaRate) || 0) / 100).toFixed(2), [subtotal, ivaRate]);
  const total = useMemo(() => +(subtotal + ivaAmount).toFixed(2), [subtotal, ivaAmount]);

  const save = async () => {
    if (!client.name.trim()) return toast.error('El nombre del cliente es obligatorio');
    if (lines.length === 0) return toast.error('Añade al menos una bicicleta');
    try {
      const payload = { client, lines, iva_rate: Number(ivaRate) || 0, notes };
      let res;
      if (savedProforma) {
        res = await axios.put(`${API}/proformas/${savedProforma.id}`, payload);
        toast.success('Proforma actualizada');
      } else {
        res = await axios.post(`${API}/proformas`, payload);
        toast.success(`Proforma ${res.data.number} creada`);
      }
      setSavedProforma(res.data);
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const exportPDF = async () => {
    if (lines.length === 0) return toast.error('Añade al menos una bicicleta');
    const logoDataUrl = await prepareLogoForPdf(settings.logo_url);
    buildPdf({ settings, client, lines, ivaRate, subtotal, ivaAmount, total, notes, number: savedProforma?.number, date: savedProforma?.date, logoDataUrl }).save(
      `proforma_${savedProforma?.number || 'borrador'}.pdf`
    );
    toast.success('PDF exportado');
  };

  const printDirect = async () => {
    if (lines.length === 0) return toast.error('Añade al menos una bicicleta');
    const logoDataUrl = await prepareLogoForPdf(settings.logo_url);
    const doc = buildPdf({ settings, client, lines, ivaRate, subtotal, ivaAmount, total, notes, number: savedProforma?.number, date: savedProforma?.date, logoDataUrl });
    doc.autoPrint();
    window.open(doc.output('bloburl'), '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-6xl max-h-[92vh] flex flex-col p-0 gap-0" data-testid="proforma-editor">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl flex items-center gap-2">
            <FileSignature size={22} className="text-orange-500" />
            {savedProforma ? `Proforma ${savedProforma.number}` : 'Nueva Proforma'}
          </DialogTitle>
          <DialogDescription>
            {savedProforma
              ? `Editar proforma · ${fmtDate(savedProforma.date)}`
              : 'Los datos fiscales de la empresa se incluyen automáticamente en el PDF.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-5">
          {/* Header preview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border-orange-200/60">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                  <Building2 size={14} /> Tu empresa
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 text-sm">
                {settings.legal_name ? (
                  <>
                    <div className="font-semibold text-slate-800">{settings.legal_name}</div>
                    {settings.cif && <div className="text-slate-500">CIF: {settings.cif}</div>}
                    {settings.address && <div className="text-slate-500"><MapPin size={12} className="inline mr-1" />{settings.address}</div>}
                    <div className="text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
                      {settings.phone && <span><Phone size={12} className="inline mr-1" />{settings.phone}</span>}
                      {settings.email && <span><Mail size={12} className="inline mr-1" />{settings.email}</span>}
                      {settings.web && <span><Globe size={12} className="inline mr-1" />{settings.web}</span>}
                    </div>
                  </>
                ) : (
                  <div className="text-slate-400 italic text-xs">
                    Aún no has configurado los datos fiscales. Ve a la pestaña "Configuración".
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Cliente</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Input placeholder="Nombre / Razón social *" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="client-name" />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="CIF / NIF" value={client.cif} onChange={(e) => setClient({ ...client, cif: e.target.value })} data-testid="client-cif" />
                  <Input placeholder="Teléfono" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} data-testid="client-phone" />
                </div>
                <Input placeholder="Dirección" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} data-testid="client-address" />
                <Input placeholder="Email" type="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="client-email" />
              </CardContent>
            </Card>
          </div>

          {/* Bike selector */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider flex items-center gap-2">
                <Plus size={14} /> Añadir bicicletas
              </CardTitle>
              <p className="text-xs text-slate-500">Selecciona un modelo y haz click en el nº de bicicleta para añadirla.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Select value={modelFilter} onValueChange={setModelFilter}>
                  <SelectTrigger className="h-9 w-64" data-testid="selector-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {models.map(m => <SelectItem key={m} value={m}>{m === 'all' ? 'Todos los modelos' : m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="relative flex-1 min-w-[180px]">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Buscar por nº, modelo, talla..."
                    value={bikeSearch}
                    onChange={(e) => setBikeSearch(e.target.value)}
                    className="pl-8 h-9"
                    data-testid="selector-search"
                  />
                </div>
                <span className="text-xs text-slate-500 flex items-center">{filteredBikes.length} disponibles</span>
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg p-2 flex flex-wrap gap-1.5" data-testid="bike-grid">
                {filteredBikes.length === 0 && (
                  <div className="text-xs text-slate-400 p-3">No hay bicicletas que coincidan.</div>
                )}
                {filteredBikes.map(b => {
                  const used = usedBikeIds.has(b.id);
                  return (
                    <button
                      type="button"
                      key={b.id}
                      disabled={used}
                      onClick={() => addLine(b)}
                      className={`px-2 py-1 rounded-md font-mono text-xs border transition-colors ${
                        used
                          ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-orange-50 hover:border-orange-300 hover:text-orange-700'
                      }`}
                      title={`${b.model} · ${b.size || ''}`}
                      data-testid={`add-bike-${b.id}`}
                    >
                      {b.number}
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Lines */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Líneas ({lines.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nº bicicleta</TableHead>
                    <TableHead>Modelo</TableHead>
                    <TableHead>Talla</TableHead>
                    <TableHead>Nº bastidor</TableHead>
                    <TableHead className="text-right">Precio neto (€)</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-6">
                        Aún no has añadido bicicletas.
                      </TableCell>
                    </TableRow>
                  ) : lines.map((l, idx) => (
                    <TableRow key={`${l.bicycle_id}-${idx}`} data-testid={`line-row-${idx}`}>
                      <TableCell className="font-mono font-semibold">{l.bicycle_number}</TableCell>
                      <TableCell>{l.model || '-'}</TableCell>
                      <TableCell>{l.size || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{l.frame_number || '-'}</TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-32 h-8 text-right ml-auto"
                          value={l.price_net}
                          onChange={(e) => updateLinePrice(idx, e.target.value)}
                          data-testid={`line-price-${idx}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLine(idx)} data-testid={`line-remove-${idx}`}>
                          <Trash2 size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Totals + notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Notas / Observaciones</Label>
              <Textarea
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Validez de la oferta, condiciones de pago..."
                data-testid="proforma-notes"
              />
            </div>
            <Card className="bg-slate-50/60">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span className="font-semibold tabular-nums" data-testid="totals-subtotal">{fmtMoney(subtotal)}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-600 flex items-center gap-2">
                    IVA
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      className="w-16 h-7 text-xs text-right"
                      value={ivaRate}
                      onChange={(e) => setIvaRate(e.target.value)}
                      data-testid="iva-rate-input"
                    />
                    %
                  </span>
                  <span className="font-semibold tabular-nums" data-testid="totals-iva">{fmtMoney(ivaAmount)}</span>
                </div>
                <div className="border-t border-slate-200 pt-2 flex justify-between text-lg">
                  <span className="font-semibold text-slate-800">TOTAL</span>
                  <span className="font-bold text-orange-600 tabular-nums" data-testid="totals-total">{fmtMoney(total)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-slate-200 flex flex-wrap gap-2">
          <Button variant="outline" onClick={onClose} data-testid="editor-cancel-btn">Cancelar</Button>
          <div className="flex gap-2 ml-auto">
            <Button variant="outline" onClick={exportPDF} className="gap-2" data-testid="editor-pdf-btn">
              <FileText size={16} /> PDF
            </Button>
            <Button variant="outline" onClick={printDirect} className="gap-2" data-testid="editor-print-btn">
              <Printer size={16} /> Imprimir
            </Button>
            <Button onClick={save} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="editor-save-btn">
              {savedProforma ? 'Actualizar' : 'Guardar'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===================== PDF Builder =====================

// Convert any logo source (data URL with svg, http URL, or already-png data URL) into a PNG data URL that jsPDF.addImage can consume. Returns '' on failure.
async function prepareLogoForPdf(src) {
  if (!src || typeof src !== 'string') return '';
  // PNG/JPEG data URL → use directly
  if (/^data:image\/(png|jpe?g)/i.test(src)) return src;
  // Anything else (SVG data URL, http(s) URL) → rasterize via canvas
  return await new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const maxW = 600;
          const ratio = img.width > maxW ? maxW / img.width : 1;
          const w = Math.round(img.width * ratio) || maxW;
          const h = Math.round(img.height * ratio) || Math.round(maxW * 0.4);
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve('');
        }
      };
      img.onerror = () => resolve('');
      img.src = src;
    } catch {
      resolve('');
    }
  });
}

function buildPdf({ settings, client, lines, ivaRate, subtotal, ivaAmount, total, notes, number, date, logoDataUrl }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  // Logo (left side) if available
  if (logoDataUrl) {
    try {
      // Detect format for addImage (PNG / JPEG)
      const fmt = /^data:image\/jpe?g/i.test(logoDataUrl) ? 'JPEG' : 'PNG';
      doc.addImage(logoDataUrl, fmt, margin, y, 32, 18, undefined, 'FAST');
    } catch {
      // Silently ignore if logo fails to embed
    }
  }

  // Title block (top right)
  doc.setFontSize(22);
  doc.setTextColor(244, 114, 22); // orange-500
  doc.text('PROFORMA', pageWidth - margin, y + 4, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(80);
  if (number) doc.text(`Nº ${number}`, pageWidth - margin, y + 11, { align: 'right' });
  doc.text(`Fecha: ${fmtDate(date || new Date().toISOString())}`, pageWidth - margin, y + 17, { align: 'right' });

  // Empresa block (left, below logo if present)
  const textStartX = logoDataUrl ? margin + 36 : margin;
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(settings.legal_name || 'Empresa sin configurar', textStartX, y + 4);
  doc.setFontSize(9);
  doc.setTextColor(80);
  let line = y + 9;
  if (settings.cif) { doc.text(`CIF: ${settings.cif}`, textStartX, line); line += 4; }
  if (settings.address) { doc.text(settings.address, textStartX, line); line += 4; }
  const extras = [];
  if (settings.phone) extras.push(settings.phone);
  if (settings.email) extras.push(settings.email);
  if (settings.web) extras.push(settings.web);
  if (extras.length) { doc.text(extras.join(' · '), textStartX, line); line += 4; }

  y = Math.max(line, y + 25) + 6;

  // Client block
  doc.setDrawColor(220);
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, pageWidth - margin * 2, 26, 'FD');
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text('CLIENTE', margin + 3, y + 5);
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text(client.name || '-', margin + 3, y + 11);
  doc.setFontSize(9);
  doc.setTextColor(80);
  const cInfo1 = [];
  if (client.cif) cInfo1.push(`CIF: ${client.cif}`);
  if (client.phone) cInfo1.push(`Tel: ${client.phone}`);
  if (client.email) cInfo1.push(client.email);
  if (cInfo1.length) doc.text(cInfo1.join(' · '), margin + 3, y + 17);
  if (client.address) doc.text(client.address, margin + 3, y + 22);

  y += 32;

  // Lines table
  autoTable(doc, {
    startY: y,
    head: [['Nº bicicleta', 'Modelo', 'Talla', 'Nº bastidor', 'Precio (€)']],
    body: lines.map(l => [
      l.bicycle_number || '-',
      l.model || '-',
      l.size || '-',
      l.frame_number || '-',
      (Number(l.price_net) || 0).toFixed(2).replace('.', ','),
    ]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 252] },
    columnStyles: {
      0: { cellWidth: 26 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 16 },
      3: { cellWidth: 38 },
      4: { cellWidth: 28, halign: 'right' },
    },
    margin: { left: margin, right: margin },
  });

  let endY = doc.lastAutoTable.finalY + 6;

  // Totals (right side)
  const totalsX = pageWidth - margin - 70;
  doc.setDrawColor(220);
  doc.rect(totalsX, endY, 70, 26);
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text('Subtotal', totalsX + 3, endY + 7);
  doc.text(`${(subtotal || 0).toFixed(2).replace('.', ',')} €`, totalsX + 67, endY + 7, { align: 'right' });
  doc.text(`IVA (${ivaRate}%)`, totalsX + 3, endY + 14);
  doc.text(`${(ivaAmount || 0).toFixed(2).replace('.', ',')} €`, totalsX + 67, endY + 14, { align: 'right' });
  doc.setFontSize(12);
  doc.setTextColor(244, 114, 22);
  doc.setFont(undefined, 'bold');
  doc.text('TOTAL', totalsX + 3, endY + 22);
  doc.text(`${(total || 0).toFixed(2).replace('.', ',')} €`, totalsX + 67, endY + 22, { align: 'right' });
  doc.setFont(undefined, 'normal');

  // Bank details (below totals — visible to facilitate transfers)
  const hasBank = (settings.bank_name || settings.iban || settings.swift_bic);
  if (hasBank) {
    const bankValues = [settings.bank_name, settings.iban, settings.swift_bic].filter(v => v && String(v).trim());
    let bankY = endY + 32;
    // Dynamic box height: top padding 5 + title 4 + gap 2 + N×5 + bottom padding 3
    const bankBoxH = 5 + 4 + 2 + bankValues.length * 5 + 3;
    doc.setDrawColor(220);
    doc.setFillColor(248, 250, 252);
    doc.rect(margin, bankY, pageWidth - margin * 2, bankBoxH, 'FD');
    // Title — bold, dark slate, slightly larger
    doc.setFontSize(10);
    doc.setTextColor(15, 23, 42);
    doc.setFont(undefined, 'bold');
    doc.text('DATOS BANCARIOS', margin + 4, bankY + 6);
    doc.setFont(undefined, 'normal');
    // Values — plain, no labels, no colons
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    bankValues.forEach((val, i) => {
      doc.text(String(val), margin + 4, bankY + 11 + 2 + i * 5);
    });
  }

  // Notes
  if (notes && notes.trim()) {
    doc.setFontSize(9);
    doc.setTextColor(80);
    doc.text('Notas:', margin, endY + 7);
    const wrapped = doc.splitTextToSize(notes, pageWidth - margin * 2 - 80);
    doc.text(wrapped, margin, endY + 12);
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(140);
  doc.text(
    'Esta proforma no constituye factura. Validez sujeta a confirmación.',
    pageWidth / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: 'center' }
  );

  return doc;
}
